
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useUser } from "@/hooks/use-user";
import { 
  useCommissionRequest, 
  useCommissionOffer,
  useUpdateCommissionRequest
} from "@/hooks/commission";
import { CommissionChat } from "@/components/commissions/CommissionChat";
import { CommissionRequestStatus } from "@/components/commissions/CommissionRequestStatus";
import { CommissionDeliveryManager } from "@/components/commissions/CommissionDelivery";
import { AlertCircle } from "lucide-react";

export default function CommissionRequestDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  
  console.log("Page: Commission request ID from params:", id);
  console.log("Page: Current user:", user);
  
  const { 
    data: request, 
    isLoading: isLoadingRequest, 
    error: requestError,
    isError: isRequestError
  } = useCommissionRequest(id || '');
  
  console.log("Page: Request data:", request);
  console.log("Page: Loading state:", isLoadingRequest);
  console.log("Page: Error state:", requestError);
  console.log("Page: Is error:", isRequestError);
  
  const {
    data: offer,
    isLoading: isLoadingOffer
  } = useCommissionOffer(request?.commission_offer_id || '');
  
  const {
    mutate: updateRequest,
    isPending: isUpdatingRequest
  } = useUpdateCommissionRequest();
  
  const handleUpdateStatus = (newStatus: string) => {
    if (!request) return;
    
    let updatedData: any = { status: newStatus };
    
    // Set delivery deadline when accepting a commission
    if (newStatus === 'accepted' && offer) {
      const deadline = new Date();
      deadline.setDate(deadline.getDate() + offer.delivery_time_days);
      updatedData.delivery_deadline = deadline.toISOString();
    }
    
    updateRequest({
      id: request.id,
      request: updatedData
    });
  };
  
  const isLoading = isLoadingRequest || isLoadingOffer;
  
  // Loading state
  if (isLoading) {
    return (
      <DashboardLayout title="Detalhes da Solicitação">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Carregando detalhes da solicitação...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  // Error or no data state
  if (isRequestError || !request) {
    console.error("Page: Request error or no request found:", requestError, request);
    return (
      <DashboardLayout title="Detalhes da Solicitação">
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <AlertCircle className="h-12 w-12 text-muted-foreground" />
          <div className="text-center">
            <h2 className="text-lg font-medium mb-2">Solicitação não encontrada</h2>
            <p className="text-muted-foreground mb-4">
              {requestError?.message || "Esta solicitação pode não existir ou foi removida"}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              ID da solicitação: {id}
            </p>
            <Button onClick={() => navigate('/commissions')}>
              Voltar para Comissões
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  // Check if user is either the artist or client
  const isAuthorized = user?.id === request.artist_id || user?.id === request.client_id;
  if (!isAuthorized) {
    console.warn("Page: User not authorized to view this commission");
    return (
      <DashboardLayout title="Acesso Negado">
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <div className="text-center">
            <h2 className="text-lg font-medium mb-2">Acesso não autorizado</h2>
            <p className="text-muted-foreground mb-4">
              Você não tem permissão para ver esta solicitação de comissão
            </p>
            <Button onClick={() => navigate('/commissions')}>
              Voltar para Comissões
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  const isArtist = user?.id === request.artist_id;
  const clientName = isArtist ? "Cliente" : "Você";
  const artistName = isArtist ? "Você" : "Artista";
  
  return (
    <DashboardLayout title={`Solicitação de Comissão #${request.id.substring(0, 8)}`}>
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Tabs defaultValue="details">
              <TabsList>
                <TabsTrigger value="details">Detalhes</TabsTrigger>
                <TabsTrigger value="messages">Mensagens</TabsTrigger>
                <TabsTrigger value="deliveries">Entregas</TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="mt-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl">Detalhes da Solicitação</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div>
                      <h3 className="font-medium mb-2">Descrição</h3>
                      <p className="text-muted-foreground whitespace-pre-wrap">{request.description}</p>
                    </div>
                    
                    {request.reference_urls && request.reference_urls.length > 0 && (
                      <div>
                        <h3 className="font-medium mb-3">Imagens de Referência</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {request.reference_urls.map((url, index) => (
                            <div key={index} className="border rounded-lg overflow-hidden">
                              <img 
                                src={url} 
                                alt={`Referência ${index + 1}`} 
                                className="w-full h-48 object-cover"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = 'https://placehold.co/300x200?text=Imagem+Inválida';
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {offer && (
                      <>
                        <Separator />
                        
                        <div>
                          <h3 className="font-medium mb-3">Oferta Original</h3>
                          <Card>
                            <CardHeader className="pb-2">
                              <CardTitle className="text-base">{offer.title}</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <p className="text-sm text-muted-foreground mb-4">{offer.description}</p>
                              <div className="flex justify-between text-sm">
                                <span>Prazo de entrega:</span>
                                <span className="font-medium">{offer.delivery_time_days} dias</span>
                              </div>
                              <div className="flex justify-between text-sm mt-1">
                                <span>Revisões incluídas:</span>
                                <span className="font-medium">{offer.max_revision}</span>
                              </div>
                            </CardContent>
                          </Card>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="messages" className="mt-6">
                <CommissionChat requestId={request.id} />
              </TabsContent>
              
              <TabsContent value="deliveries" className="mt-6">
                <CommissionDeliveryManager request={request} />
              </TabsContent>
            </Tabs>
          </div>
          
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Status da Comissão</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <CommissionRequestStatus 
                  request={request}
                  onUpdateStatus={handleUpdateStatus}
                  isUpdating={isUpdatingRequest}
                />
                
                <Separator />
                
                <div>
                  <h3 className="text-sm font-medium mb-3">Participantes</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback>A</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{artistName}</p>
                        <p className="text-xs text-muted-foreground">Artista</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarFallback>C</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{clientName}</p>
                        <p className="text-xs text-muted-foreground">Cliente</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
